import { TrashIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button";
import DatePickerInput from "~/components/formElements/DatePickerInput";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import Input from "~/components/formElements/Input";
import { RadioGroupPlain } from "~/components/formElements/RadioGroup/RadioGroup.tsx";
import RestrictedNumberInput from "~/components/formElements/RestrictedNumberInput";
import SubmitButton from "~/components/formElements/SubmitButton";
import Textarea from "~/components/formElements/Textarea";
import Headline from "~/components/Headline";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import Tooltip from "~/components/Tooltip";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import {
	updateDraftNonProjectInvoiceStatusToSent,
	updateNonProjectInvoice,
} from "~/modules/billing/api/draftInvoice/draftInvoiceApiDispatchers.ts";
import InvoiceTotalsFormSection from "~/modules/billing/components/InvoiceTotalsFormSection";
import type { UnprocessedInvoicesIndexTableData } from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";
import { DEFAULT_PAYMENT_TARGET_DAYS } from "~/modules/billing/constants/billingConstants.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type FormData = {
	comment: string | null;
	deliveryEndDate: Date;
	deliveryStartDate: Date;
	description: string | null;
	invoiceSentAt: Date | null;
	paymentTargetDays: number;
	recipientInformation: string | null;
	subtotalCents: number;
	vatPercentage: number;
}

type Props = {
	onCancel: () => void;
	onDelete?: () => void;
	onSuccess: (billingProcessWasFinished: boolean) => void;
	draftInvoiceData: UnprocessedInvoicesIndexTableData;
	userCanManageInvoices: boolean;
};

type ModeOption = "update" | "finish-billing-process";

const today = new Date();

const UpdateOrFinishNonProjectInvoiceForm = ({
	onCancel,
	onDelete,
	onSuccess,
	draftInvoiceData,
	userCanManageInvoices
}: Props) => {
	const [mode, setMode] = useState<ModeOption>("update");
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const isUpdateMode = mode === "update";
	const schema = useMemo(() => {
		return yup.object({
			comment: yup.string().nullable().default(""),
			description: yup.string().required(),
			deliveryStartDate: yup.date().required(),
			deliveryEndDate: yup
				.date()
				.required()
				.min(yup.ref("deliveryStartDate"), "Das Startdatum muss vor dem Enddatum liegen."),
			paymentTargetDays: yup.number().required(),
			invoiceSentAt: mode === "finish-billing-process"
				? yup.date().required()
				: yup.date().nullable().default(null),
			recipientInformation: yup.string().nullable().default(""),
			subtotalCents: yup.number().required().min(1, "Der Rechnungsbetrag darf nicht 0,00 € sein."),
			vatPercentage: yup.number().required(),
		});
	}, [mode]);
	const defaultValues = useMemo(() => {
		return {
			comment: draftInvoiceData.comment || "",
			description: draftInvoiceData.projectTitle || "",
			deliveryStartDate: draftInvoiceData.deliveryStartDate,
			deliveryEndDate: draftInvoiceData.deliveryEndDate,
			paymentTargetDays: DEFAULT_PAYMENT_TARGET_DAYS,
			recipientInformation: draftInvoiceData.recipientInformation || "",
			subtotalCents: draftInvoiceData.invoiceTotalCents,
			vatPercentage: draftInvoiceData.vatPercentage,
			invoiceSentAt: today,
		};
	}, [draftInvoiceData.comment, draftInvoiceData.deliveryEndDate, draftInvoiceData.deliveryStartDate, draftInvoiceData.invoiceTotalCents, draftInvoiceData.projectTitle, draftInvoiceData.recipientInformation, draftInvoiceData.vatPercentage]);

	const {
		handleSubmit,
		control,
		formState: { isSubmitted, isValid, isDirty, errors },
		trigger,
		watch,
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
	});

	useEffect(() => {
		trigger();
	}, [mode, trigger]);

	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);
			const invoiceData = {
				...data,
				deliveryEndDate: formatDateToYYYYMMDD(data.deliveryEndDate),
				deliveryStartDate: formatDateToYYYYMMDD(data.deliveryStartDate),
			};

			if (isUpdateMode) {
				await updateNonProjectInvoice(draftInvoiceData.id, invoiceData);
				onSuccess(false);
			} else {
				const statusUpdateData = {
					...invoiceData,
					invoiceSentAt: formatDateToYYYYMMDD(invoiceData.invoiceSentAt!),
				};
				await updateDraftNonProjectInvoiceStatusToSent(draftInvoiceData.id, statusUpdateData);
				onSuccess(mode === "finish-billing-process");
			}

		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
			setIsBusy(false);
		}
	}, [draftInvoiceData.id, isUpdateMode, mode, onSuccess]);


	const formIsSubmittable = useFormIsSubmittable({ isDirty, isSubmitted, isValid, isLoading: isBusy });
	const radioGroupOptions = useMemo(() => {
		return [{
			value: "update",
			label: "Rechnungsdaten aktualisieren",
		}, {
			value: "finish-billing-process",
			label: "Rechnungsstellung abschließen",
		}];
	}, []);
	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<div className="flex items-center justify-between">
					<SidebarHeaderHeadline>Nicht projektbezogene Rechnung</SidebarHeaderHeadline>
					<Tooltip text="Rechnung löschen">
						<div>
							<Button theme="danger"
									onClick={onDelete}
									size="sm">
								<TrashIcon className="size-5" />
							</Button>
						</div>
					</Tooltip>
				</div>
			</SidebarHeader>
			<div className="grow gap-x-8 overflow-auto px-7 py-2">
				{isBusy && <SidebarBusyOverlay />}
				{serverErrorMessage &&
					<SidebarErrorOverlay title="Speichern fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}

				<div className="grid h-auto grid-cols-2 gap-4 pt-1">
					<div className="col-span-2">
						<RadioGroupPlain name=""
										 numCols={2}
										 options={radioGroupOptions}
										 value={mode}
										 onChange={(value) => setMode(value as ModeOption)} />
					</div>
					<div className="col-span-2">
						<Headline className="mb-2"
								  type="h4">Beschreibung</Headline>
						<Input control={control}
							   name="description"
							   placeholder="Kurze Beschreibung zum Inhalt der Rechnung"
						/>
					</div>
					{isUpdateMode && <div className="col-span-2 mt-4">
						<Headline className="mb-2"
								  type="h4">Empfänger</Headline>
						<Input control={control}
							   name="recipientInformation"
							   placeholder="Name, Versandart, ggf. E-Mail-Adresse"
						/>
					</div>}
					{!isUpdateMode && <div className="col-span-2 mt-4">
						<div className="col-span-2">
							<Headline className="mb-2"
									  type="h4">Empfänger / Zahlungsziel</Headline>
							<Input control={control}
								   label="Versendet an"
								   name="recipientInformation"
								   placeholder="Name, Versandart, ggf. E-Mail-Adresse"
							/>
						</div>
						<div className="mt-4 grid grid-cols-2 gap-x-4">
							<DatePickerInput name="invoiceSentAt"
											 label="Versendet am / Rechnungsdatum"
											 control={control}
											 minDate={today} />
							<RestrictedNumberInput control={control}
												   minValue={0}
												   maxValue={999999}
												   label="Zahlungsziel in Tagen"
												   name="paymentTargetDays"
												   placeholder="Anzahl Tage"
							/>
						</div>
					</div>}
					<div className="col-span-2 mt-4 grid grid-cols-2 gap-x-4">
						<Headline className="col-span-2 mb-2"
								  type="h4">Leistungszeitraum</Headline>
						<DatePickerInput name="deliveryStartDate"
										 control={control}
										 clearable={false}
										 label="Startdatum" />
						<DatePickerInput name="deliveryEndDate"
										 control={control}
										 clearable={false}
										 label="Enddatum" />
					</div>
					<div className="h-1" />
					<InvoiceTotalsFormSection<FormData> control={control}
														errorMessage={errors?.subtotalCents?.message}
														title="Rechnungsbetrag"
														watch={watch}
														includeTravelCosts={false}
					/>
					<div className="col-span-2 mt-4">
						<Headline className="mb-2"
								  type="h4">Kommentar</Headline>
						<Textarea control={control}
								  name="comment"
								  placeholder="Hier können Besonderheiten zum Abrechnungsprozess vermerkt werden."
						/>
					</div>
					{!isUpdateMode && <div className="col-span-2 mt-4">
						<Headline className="mb-2"
								  type="h4">Rechnungsnummer</Headline>
						<p className="leading-tight text-gray-800">Die Rechnungsnummer wird beim Absenden dieses
							Formulars automatisch erzeugt und muss anschliessend im Rechnungsdokument nachgetragen
							werden.</p>
					</div>}
				</div>
			</div>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				{userCanManageInvoices && <SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					{isUpdateMode ? "Rechnungdaten aktualisieren" : "Rechnungsstellung abschließen"}
				</SubmitButton>}
				<Button theme="white"
						onClick={onCancel}>
					{userCanManageInvoices ? "Abbrechen" :"Schließen"}
				</Button>
			</SidebarFooter>
		</form>);
};

export default UpdateOrFinishNonProjectInvoiceForm;
